import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Check, Pause, Play, TriangleAlert } from 'lucide-react';
import { ChipGroup } from '@/components/ui/ChipGroup';
import { PeopleRow } from '@/components/ui/PeopleRow';
import { useT } from '@/lib/i18n/useT';
import { usePrefsStore } from '@/lib/prefs/usePrefsStore';
import { cn } from '@/lib/cn';
import type { Throughput } from './engine';
import { RUSH_PEAK, arrivalRate, rushVerdict, runDay, startDay, step, waitMinutes, type Crowd, type SimInput, type SimState } from './simulate';

export interface RushHourSimulatorProps {
  /** El modo de público vive en la página: sobrevive al remonte por `key` (no se guarda en el borrador). */
  crowd: Crowd;
  onCrowdChange: (crowd: Crowd) => void;
  capacity: Throughput;
  /** Jugadores de un evento. */
  players: number;
  /** Minutos que dura el día. */
  dayMinutes: number;
  minutesPerHandout: number;
  claimRate: number;
  /** Pedido por evento (el de la tarjeta). */
  orderPerEvent: number;
  /** Hora de apertura, en minutos desde medianoche. */
  startMinutes: number;
  /** Sin evento: suma N puntos de entrega a la entrada. */
  onAddStations?: (n: number) => void;
  /** Con evento: dónde se añaden. */
  addStationsHref?: string;
}

const MAX_LANES = 6;
const MAX_FIGURES = 14;
const DAY_MS = 15000;

/** Mover el día sin animación si el sistema lo pide. Hook local: no ata el planner a la zona de juegos. */
function usePrefersReducedMotion(): boolean {
  const [reduced, setReduced] = useState(() => typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const on = () => setReduced(mq.matches);
    mq.addEventListener('change', on);
    return () => mq.removeEventListener('change', on);
  }, []);
  return reduced;
}

/**
 * Un día de evento a cámara rápida (S1b). Se monta con `key` = las entradas que lo mueven (D-12): al
 * cambiar una, se remonta y el intervalo muere en la limpieza del efecto. El número del veredicto lo da
 * el motor (`rushVerdict`), no la cola dibujada.
 */
export function RushHourSimulator({
  crowd,
  onCrowdChange,
  capacity,
  players,
  dayMinutes,
  minutesPerHandout,
  claimRate,
  orderPerEvent,
  startMinutes,
  onAddStations,
  addStationsHref,
}: RushHourSimulatorProps) {
  const t = useT();
  const language = usePrefsStore((s) => s.language);
  const reduced = usePrefersReducedMotion();
  const points = capacity.points;
  const input: SimInput = useMemo(
    () => ({ players, dayMinutes, minutesPerHandout, stations: points, crowd }),
    [players, dayMinutes, minutesPerHandout, points, crowd],
  );
  const [sim, setSim] = useState<SimState>(() => startDay({ players, dayMinutes, minutesPerHandout, stations: points, crowd }));
  const [running, setRunning] = useState(false);
  const over = sim.t >= dayMinutes;
  // Todo el día dura ~15 s sea cual sea su duración: ≤ 187 tics de 15 000 ms / tics.
  const perTic = Math.max(1, Math.ceil(dayMinutes / 187));
  const ticMs = DAY_MS / Math.ceil(dayMinutes / perTic);

  useEffect(() => {
    if (!running) return undefined;
    const id = setInterval(() => {
      setSim((s) => {
        let next = s;
        for (let i = 0; i < perTic && next.t < dayMinutes; i += 1) next = step(input, next);
        return next;
      });
    }, ticMs);
    return () => clearInterval(id);
  }, [running, input, perTic, dayMinutes, ticMs]);

  useEffect(() => {
    if (running && over) setRunning(false);
  }, [running, over]);

  const reset = (next: Crowd = crowd) => {
    setRunning(false);
    setSim(startDay({ ...input, crowd: next }));
  };
  const pickCrowd = (next: Crowd) => {
    onCrowdChange(next);
    reset(next);
  };
  const toggle = () => {
    if (reduced) {
      setSim(runDay(input));
      setRunning(false);
    } else if (over) {
      setSim(startDay(input));
      setRunning(true);
    } else setRunning((r) => !r);
  };

  const verdict = rushVerdict(capacity, crowd);
  const playedAll = sim.served.reduce((a, b) => a + b, 0);
  const left = Math.max(0, orderPerEvent - Math.round(playedAll * claimRate));
  const fmt = (n: number) => n.toLocaleString(language);
  const clock = (() => {
    const total = startMinutes + sim.t;
    const d = new Date(1970, 0, 1, Math.floor(total / 60) % 24, total % 60);
    return d.toLocaleTimeString(language, { hour: 'numeric', minute: '2-digit' });
  })();
  const pctDay = Math.min(100, (sim.t / dayMinutes) * 100);
  const bars = Array.from({ length: 24 }, (_, k) => {
    const f = arrivalRate(crowd, (k + 0.5) / 24);
    return { h: Math.round((f / RUSH_PEAK) * 100), on: (k / 24) * dayMinutes <= sim.t };
  });
  const shown = sim.queues.slice(0, MAX_LANES);
  const hidden = sim.queues.slice(MAX_LANES);
  const playWord = over ? 'merchPlanner.sim.again' : sim.t > 0 ? 'merchPlanner.sim.resume' : 'merchPlanner.sim.play';
  const panel = 'rounded-xl bg-[var(--color-bg-surface)]';

  return (
    <section
      data-surface="night"
      data-testid="planner-sim"
      className="flex flex-col gap-4 rounded-[18px] bg-[var(--color-xk-bg-nav)] p-5 text-[var(--color-fg-primary)]"
    >
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex flex-col gap-1">
          <h2 className="text-lg font-bold">{t('merchPlanner.sim.title')}</h2>
          <p className="text-[13px] text-[var(--color-fg-muted)]">{t('merchPlanner.sim.subtitle')}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <ChipGroup
            label={t('merchPlanner.sim.crowdLabel')}
            value={crowd}
            onChange={pickCrowd}
            options={[
              { value: 'steady', label: t('merchPlanner.sim.steady') },
              { value: 'rush', label: t('merchPlanner.sim.rush') },
            ]}
          />
          <button
            type="button"
            data-testid="sim-play"
            onClick={toggle}
            className="inline-flex h-11 items-center gap-2 rounded-[10px] bg-[var(--color-xk-accent)] px-4 text-sm font-bold text-[var(--color-fg-on-accent)] transition-colors hover:bg-[var(--color-xk-accent-hover)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-fg-primary)] motion-reduce:transition-none"
          >
            {running ? <Pause className="h-4 w-4" aria-hidden="true" /> : <Play className="h-4 w-4" aria-hidden="true" />}
            {running ? t('merchPlanner.sim.pause') : t(playWord)}
          </button>
          <button
            type="button"
            data-testid="sim-reset"
            onClick={() => reset()}
            className="inline-flex h-11 items-center rounded-[10px] border border-[var(--color-border-strong)] px-4 text-sm font-medium transition-colors hover:bg-[var(--color-bg-elevated)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-fg-primary)] motion-reduce:transition-none"
          >
            {t('merchPlanner.sim.reset')}
          </button>
        </div>
      </div>
      {reduced ? <p className="text-xs text-[var(--color-fg-muted)]">{t('merchPlanner.sim.reducedMotion')}</p> : null}

      <div className="flex flex-col gap-3.5 sm:flex-row sm:items-stretch">
        <div className={cn(panel, 'flex min-w-[150px] flex-col gap-0.5 px-4 py-3')}>
          <span className="text-[11px] uppercase tracking-[1px] text-[var(--color-fg-muted)]">{t('merchPlanner.sim.clock')}</span>
          <span className="font-[var(--font-dash-mono)] text-[32px] font-bold leading-tight tabular-nums text-[var(--color-accent-ink)]">{clock}</span>
        </div>
        <div className={cn(panel, 'flex flex-1 flex-col justify-center gap-2 px-4 py-3')}>
          <div className="flex justify-between text-xs text-[var(--color-fg-muted)]">
            <span>{t('merchPlanner.sim.doorsOpen')}</span>
            <span>{t('merchPlanner.sim.progress', { n: Math.round(pctDay) })}</span>
            <span>{t('merchPlanner.sim.close')}</span>
          </div>
          <div className="h-2.5 overflow-hidden rounded-full bg-[var(--color-border-default)]">
            <div className="h-full bg-[var(--color-xk-accent)]" style={{ width: `${pctDay}%` }} />
          </div>
          <div className="flex h-7 items-end gap-0.5" aria-hidden="true">
            {bars.map((b, k) => (
              <div
                key={k}
                style={{ height: `${b.h}%` }}
                className={cn('flex-1 rounded-t-sm', b.on ? 'bg-[var(--color-xk-accent)]' : 'bg-[var(--color-border-strong)]')}
              />
            ))}
          </div>
        </div>
      </div>

      <div className="flex flex-col gap-2.5">
        {shown.map((q, i) => {
          const wait = waitMinutes(q, minutesPerHandout);
          return (
            <div key={i} data-testid="sim-lane" className={cn(panel, 'flex items-center gap-3.5 px-3.5 py-3')}>
              <div className="flex w-28 shrink-0 flex-col gap-0.5">
                <span className="text-sm font-bold">{t('merchPlanner.sim.station', { n: i + 1 })}</span>
                <span className="text-xs text-[var(--color-fg-muted)]">{t('merchPlanner.sim.served', { n: fmt(sim.served[i]) })}</span>
              </div>
              <div className="flex min-h-[30px] flex-1 flex-wrap items-center gap-1.5">
                <PeopleRow total={Math.min(q, MAX_FIGURES)} filled={Math.min(q, MAX_FIGURES)} size="sm" highlightFirst />
                <span className="font-[var(--font-dash-mono)] text-[13px] font-bold">
                  {q > MAX_FIGURES ? `+${q - MAX_FIGURES}` : q === 0 ? t('merchPlanner.sim.noLine') : ''}
                </span>
              </div>
              <div className="flex w-28 shrink-0 flex-col gap-0.5 text-right">
                <span
                  className={cn(
                    'font-[var(--font-dash-mono)] text-lg font-bold tabular-nums',
                    wait >= 15 ? 'text-[var(--color-error)]' : wait >= 6 ? 'text-[var(--color-warn)]' : 'text-[var(--color-accent-ink)]',
                  )}
                >
                  {wait === 0 ? t('merchPlanner.sim.noWait') : t('merchPlanner.sim.wait', { n: wait })}
                </span>
                <span className="text-[11px] text-[var(--color-fg-muted)]">{t('merchPlanner.sim.waitCaption')}</span>
              </div>
            </div>
          );
        })}
        {hidden.length > 0 ? (
          <p data-testid="sim-lane" className={cn(panel, 'px-3.5 py-3 text-sm text-[var(--color-fg-muted)]')}>
            {t('merchPlanner.sim.moreStations', { n: hidden.length, q: hidden.reduce((a, b) => a + b, 0) })}
          </p>
        ) : null}
      </div>

      <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-3">
        <div className={cn(panel, 'flex flex-col gap-0.5 px-3.5 py-3')}>
          <span className="text-xs text-[var(--color-fg-muted)]">{t('merchPlanner.sim.played')}</span>
          <span data-testid="sim-played" className="font-[var(--font-dash-mono)] text-2xl font-bold tabular-nums">{fmt(playedAll)}</span>
        </div>
        <div className={cn(panel, 'flex flex-col gap-0.5 px-3.5 py-3')}>
          <span className="text-xs text-[var(--color-fg-muted)]">{t('merchPlanner.sim.merchLeft')}</span>
          <span data-testid="sim-merch-left" className="font-[var(--font-dash-mono)] text-2xl font-bold tabular-nums text-[var(--color-accent-ink)]">{fmt(left)}</span>
        </div>
        <div className={cn(panel, 'flex flex-col gap-0.5 px-3.5 py-3')}>
          <span className="text-xs text-[var(--color-fg-muted)]">{t('merchPlanner.sim.longestLine')}</span>
          <span data-testid="sim-longest-line" className="font-[var(--font-dash-mono)] text-2xl font-bold tabular-nums">
            {t('merchPlanner.sim.people', { n: fmt(sim.longestLine) })}
          </span>
        </div>
      </div>

      <div
        data-testid="sim-verdict"
        className={cn(
          'flex flex-wrap items-center gap-3 rounded-xl px-4 py-3.5 text-[var(--color-fg-on-accent)]',
          verdict.covered ? 'bg-[var(--color-xk-accent)]' : 'bg-[var(--color-warn)]',
        )}
      >
        {verdict.covered ? <Check className="h-5 w-5 shrink-0" aria-hidden="true" /> : <TriangleAlert className="h-5 w-5 shrink-0" aria-hidden="true" />}
        <span className="min-w-0 flex-1 text-[15px] font-medium">
          {verdict.covered
            ? points === 1
              ? t('merchPlanner.sim.okOne')
              : t('merchPlanner.sim.okMany', { n: points })
            : t('merchPlanner.sim.short', { peak: verdict.peakPerHour, capacity: verdict.capacityPerHour })}
        </span>
        {!verdict.covered && onAddStations ? (
          <button
            type="button"
            onClick={() => onAddStations(verdict.extra)}
            className="inline-flex h-11 items-center rounded-[10px] bg-[var(--color-xk-bg-nav)] px-3.5 text-sm font-medium text-[var(--color-fg-primary)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-fg-on-accent)]"
          >
            {verdict.extra === 1 ? t('merchPlanner.sim.addOne') : t('merchPlanner.sim.addStations', { n: verdict.extra })}
          </button>
        ) : null}
        {!verdict.covered && !onAddStations && addStationsHref ? (
          <Link
            to={addStationsHref}
            className="inline-flex h-11 items-center rounded-[10px] bg-[var(--color-xk-bg-nav)] px-3.5 text-sm font-medium text-[var(--color-fg-primary)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-fg-on-accent)]"
          >
            {t('merchPlanner.sim.addInPlanBa')}
          </Link>
        ) : null}
      </div>
      <p aria-live="polite" className="sr-only">
        {over ? t('merchPlanner.sim.dayOver', { played: fmt(playedAll), line: fmt(sim.longestLine) }) : ''}
      </p>
    </section>
  );
}
